/**
 * Equity of a known hand against one or more weighted opponent ranges, on a board.
 *
 * This is the number every postflop decision is built on, so it has to be both honest
 * and fast enough to run several times per bot action inside a browser tab.
 *
 * Honest means: card removal is respected everywhere. The board is removed from every
 * range, the hero's own cards are removed from every range, and when there are several
 * opponents their sampled hands are removed from each other. A bot that lets two
 * villains hold the same ace is a bot that is wrong about how often it is beaten.
 *
 * Fast enough means Monte Carlo, with two exceptions taken exactly:
 *   - on the river there is nothing to run out, so a single opponent is enumerated in
 *     full and the answer is exact rather than sampled;
 *   - a fully specified opponent hand with a full board is one evaluator call.
 *
 * The estimate is returned WITH its standard error. A caller that treats 0.51 ± 0.03
 * as "ahead" is making the mistake this field exists to prevent.
 */
import { evaluate7 } from "../pipeline/solve/evaluator";
import { mulberry32 } from "../pipeline/solve/equity";
import { Range, comboCards, sampleCombo } from "./range";
import type { Card } from "./types";

export interface EquityResult {
  /** Share of the pot won on average: a win is 1, a two-way chop 0.5. */
  equity: number;
  /** Standard error of `equity`. Zero when the answer was enumerated exactly. */
  se: number;
  /** True when nothing was sampled. */
  exact: boolean;
  /** Trials actually run — diagnostics, and the denominator behind `se`. */
  trials: number;
}

const DEAD = new Uint8Array(52);

/**
 * Equity of `hole` against `villains` weighted ranges, given `board`.
 *
 * `trials` is a ceiling, not a promise: exact paths ignore it, and a range that has
 * been narrowed to almost nothing may run out of legal combos first.
 */
export function equityVsRanges(
  hole: readonly Card[],
  board: readonly Card[],
  villains: readonly Range[],
  trials = 400,
  seed = 0x5eed,
): EquityResult {
  if (hole.length !== 2) throw new Error("hero must hold exactly two cards");
  if (board.length > 5) throw new Error(`board of ${board.length} cards`);
  if (villains.length === 0) throw new Error("equity against nobody is not a question");

  const dead = new Uint8Array(52);
  for (const c of hole) {
    if (dead[c]) throw new Error("duplicate card in hero's hand");
    dead[c] = 1;
  }
  for (const c of board) {
    if (dead[c]) throw new Error("board card duplicates a hole card");
    dead[c] = 1;
  }

  // Every villain range with the known cards removed, once, before sampling.
  const known = [...hole, ...board];
  const prepared = villains.map((r) => r.clone().removeCards(known));
  const samplers = prepared.map((r) => r.sampler());
  if (samplers.some((s) => s.total <= 0)) {
    /**
     * A range with nothing left in it is not equity 1 — it is a question that cannot
     * be asked, usually because the caller narrowed a range past the point where the
     * board leaves any combos. Saying "you win" here would have the bot shove into a
     * villain it had simply lost track of.
     */
    throw new Error("a villain range has no combos left after card removal");
  }

  if (board.length === 5 && villains.length === 1) {
    return enumerateRiver(hole, board, prepared[0]!);
  }

  const rng = mulberry32(seed);
  const deck: Card[] = [];
  for (let c = 0; c < 52; c++) if (!dead[c]) deck.push(c);

  const need = 5 - board.length;
  const hand = new Array<Card>(7);
  const villainHand = new Array<Card>(7);
  const taken = new Uint8Array(52);

  let sum = 0, sumSq = 0, ran = 0;
  const full = [...board];

  for (let t = 0; t < trials; t++) {
    taken.set(DEAD);
    let clash = false;

    /**
     * One draw per villain, and a collision ends the trial.
     *
     * NOT a retry loop. Retrying until a villain's hand fits produces the sequential
     * conditional P(h1,h2) proportional to w1(h1) * w2(h2) / (W2 - blocked by h1) —
     * villain one's distribution is silently reweighted by how much of villain two's
     * range each of its hands blocks, and the estimate is biased. With narrow ranges
     * the effect is enormous: hero QQ against {AA, 76o} and {AA, KQs, 54o} enumerates
     * to 0.4615 and the retry version returned 0.4199, fifty standard errors away,
     * because it drew AA for the first villain a third of the time when the correct
     * share is 0.269.
     *
     * Discarding instead is unbiased: rejection sampling from the product measure,
     * conditioned on the hands being disjoint, is exactly the distribution we want.
     * It costs trials on tight ranges, which is why `ran` is counted separately and
     * reported.
     */
    const picks: [Card, Card][] = [];
    for (const s of samplers) {
      const id = sampleCombo(s, rng());
      if (id < 0) { clash = true; break; }
      const [a, b] = comboCards(id);
      if (taken[a] || taken[b]) { clash = true; break; }
      taken[a] = 1; taken[b] = 1;
      picks.push([a, b]);
    }
    if (clash) continue;

    // Run the board out from the cards nobody holds.
    full.length = board.length;
    for (let i = 0; i < need; i++) {
      let c = -1;
      for (let attempt = 0; attempt < 32; attempt++) {
        const pick = deck[(rng() * deck.length) | 0]!;
        if (taken[pick]) continue;
        c = pick;
        break;
      }
      if (c < 0) { clash = true; break; }
      taken[c] = 1;
      full.push(c);
    }
    if (clash) continue;

    hand[0] = hole[0]!; hand[1] = hole[1]!;
    for (let i = 0; i < 5; i++) hand[2 + i] = full[i]!;
    const mine = evaluate7(hand);

    let better = 0, tied = 0;
    for (const p of picks) {
      villainHand[0] = p[0]; villainHand[1] = p[1];
      for (let i = 0; i < 5; i++) villainHand[2 + i] = full[i]!;
      const theirs = evaluate7(villainHand);
      if (theirs > mine) { better++; break; }
      if (theirs === mine) tied++;
    }
    const share = better > 0 ? 0 : 1 / (1 + tied);
    sum += share; sumSq += share * share; ran++;
  }

  if (ran === 0) throw new Error("no equity trial could be dealt");
  const equity = sum / ran;
  // Sample standard error of the mean. With one trial the variance is undefined, and
  // reporting 0 there would claim certainty we do not have.
  const variance = ran > 1 ? Math.max(0, (sumSq - ran * equity * equity) / (ran - 1)) : 0.25;
  return { equity, se: Math.sqrt(variance / ran), exact: false, trials: ran };
}

/** The river against one range: every combo, weighted, no sampling. */
function enumerateRiver(hole: readonly Card[], board: readonly Card[], villain: Range): EquityResult {
  const hand = [hole[0]!, hole[1]!, ...board];
  const mine = evaluate7(hand);
  const theirHand = [0, 0, ...board];

  let num = 0, den = 0, counted = 0;
  for (const id of villain.ids()) {
    const w = villain.weight(id);
    const [a, b] = comboCards(id);
    theirHand[0] = a; theirHand[1] = b;
    const theirs = evaluate7(theirHand);
    num += w * (theirs > mine ? 0 : theirs === mine ? 0.5 : 1);
    den += w;
    counted++;
  }
  if (den <= 0) throw new Error("a villain range has no combos left after card removal");
  return { equity: num / den, se: 0, exact: true, trials: counted };
}

/**
 * Equity against a single opponent drawing at random from what is left of the deck.
 * The baseline "how strong is this actually" number, independent of any read.
 */
export function equityVsRandom(
  hole: readonly Card[], board: readonly Card[], trials = 400, seed = 0x5eed
): EquityResult {
  return equityVsRanges(hole, board, [Range.full()], trials, seed);
}
